import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package, Plus, Trash2, AlertTriangle, Check, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = { equipment: "Equipment", material: "Material", chemical: "Chemical", tool: "Tool" };
const UNITS = ["each", "sqft", "lnft", "gallon", "lb", "box", "roll", "set"];

export default function JobInventory({ requestId }) {
  const [items, setItems] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [leftOnSite, setLeftOnSite] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [truckItems, setTruckItems] = useState([]);
  const [form, setForm] = useState({
    name: "", quantity: "1", unit: "each", category: "material",
    cost_per_unit: "", serial_number: "", notes: "", left_on_site: false
  });

  const fetch = async () => {
    try {
      const [invRes, truckRes] = await Promise.all([
        API.get(`/inventory/job/${requestId}`),
        API.get("/inventory/truck"),
      ]);
      setItems(invRes.data.items);
      setTotalCost(invRes.data.total_cost);
      setLeftOnSite(invRes.data.left_on_site_count);
      setTruckItems(truckRes.data.items);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetch(); }, [requestId]);

  const addItem = async () => {
    if (!form.name.trim()) { toast.error("Item name required"); return; }
    try {
      await API.post(`/inventory/job/${requestId}`, {
        ...form,
        quantity: parseFloat(form.quantity) || 1,
        cost_per_unit: parseFloat(form.cost_per_unit) || 0,
      });
      toast.success("Item added");
      setShowForm(false);
      setForm({ name: "", quantity: "1", unit: "each", category: "material", cost_per_unit: "", serial_number: "", notes: "", left_on_site: false });
      fetch();
    } catch (err) { toast.error("Failed"); }
  };

  const removeItem = async (id) => {
    try { await API.delete(`/inventory/${id}`); toast.success("Removed"); fetch(); }
    catch { toast.error("Failed"); }
  };

  const toggleLeftOnSite = async (item) => {
    try {
      await API.put(`/inventory/${item.id}`, { left_on_site: !item.left_on_site });
      fetch();
    } catch { toast.error("Failed"); }
  };

  const addFromTruck = (ti) => {
    setForm(p => ({ ...p, name: ti.name, category: ti.category, unit: ti.default_unit, cost_per_unit: String(ti.default_cost || "") }));
    setShowForm(true);
  };

  return (
    <div className="space-y-3" data-testid="job-inventory">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-zinc-400" />
          <h4 className="text-sm font-heading font-semibold text-zinc-900">
            Inventory ({items.length})
          </h4>
          {totalCost > 0 && <Badge className="bg-zinc-100 text-zinc-700 text-[9px]">${totalCost}</Badge>}
          {leftOnSite > 0 && <Badge className="bg-amber-100 text-amber-700 text-[9px]">{leftOnSite} on site</Badge>}
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowForm(!showForm)}
          className="text-xs h-7" data-testid="add-inventory-btn">
          {showForm ? "Cancel" : <><Plus className="w-3 h-3 mr-1" /> Add</>}
        </Button>
      </div>

      {showForm && (
        <Card className="border border-zinc-200" data-testid="inventory-form">
          <CardContent className="p-3 space-y-2">
            {truckItems.length > 0 && (
              <div>
                <label className="text-[10px] text-zinc-500 font-bold">Quick Add from Truck:</label>
                <div className="flex gap-1 flex-wrap mt-1">
                  {truckItems.slice(0, 8).map(ti => (
                    <button key={ti.id} onClick={() => addFromTruck(ti)}
                      className="px-2 py-1 text-[10px] bg-blue-50 text-blue-700 rounded-full border border-blue-200 hover:bg-blue-100">
                      {ti.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Item name" className="w-full px-2 py-1.5 border border-zinc-300 rounded-lg text-xs"
              data-testid="inv-name-input" />
            <div className="grid grid-cols-4 gap-1.5">
              <input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
                placeholder="Qty" className="px-2 py-1.5 border border-zinc-300 rounded-lg text-xs" />
              <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}
                className="px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white">
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white">
                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input type="number" step="0.01" value={form.cost_per_unit} onChange={e => setForm(p => ({ ...p, cost_per_unit: e.target.value }))}
                placeholder="$/unit" className="px-2 py-1.5 border border-zinc-300 rounded-lg text-xs" />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-zinc-600">
                <input type="checkbox" checked={form.left_on_site}
                  onChange={e => setForm(p => ({ ...p, left_on_site: e.target.checked }))} />
                Left on site
              </label>
              <input value={form.serial_number} onChange={e => setForm(p => ({ ...p, serial_number: e.target.value }))}
                placeholder="Serial # (optional)" className="flex-1 px-2 py-1 border border-zinc-300 rounded-lg text-[10px]" />
            </div>
            <Button size="sm" onClick={addItem} className="w-full text-xs h-8 bg-zinc-900 text-white"
              data-testid="save-inventory-item">Add Item</Button>
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg border border-zinc-100"
              data-testid={`inv-item-${item.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-zinc-800">{item.name}</span>
                  <span className="text-[10px] text-zinc-400">{item.quantity} {item.unit}</span>
                  {item.left_on_site && (
                    <Badge className="bg-amber-100 text-amber-700 text-[8px]">
                      <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> On Site
                    </Badge>
                  )}
                </div>
                <span className="text-[10px] text-zinc-400">{CATEGORIES[item.category]} {item.total_cost > 0 && `- $${item.total_cost}`}</span>
              </div>
              <button onClick={() => toggleLeftOnSite(item)}
                className={`p-1 rounded ${item.left_on_site ? "text-amber-600" : "text-zinc-300"}`}>
                <AlertTriangle className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => removeItem(item.id)} className="p-1 text-red-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && !showForm && (
        <div className="text-center py-3 border border-dashed border-zinc-200 rounded-lg">
          <Package className="w-5 h-5 text-zinc-300 mx-auto mb-1" />
          <p className="text-[10px] text-zinc-400">No items logged</p>
        </div>
      )}
    </div>
  );
}
